import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "lmb_cookie_consent_v1";
const GA_ID = process.env.REACT_APP_GA_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;

function hasConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const v = JSON.parse(raw);
    return v.choice === "accepted";
  } catch {
    return false;
  }
}

function loadGA() {
  if (!GA_ID || window.__gaLoaded) return;
  window.__gaLoaded = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { anonymize_ip: true, send_page_view: false });
}

function loadClarity() {
  if (!CLARITY_ID || window.__clarityLoaded) return;
  window.__clarityLoaded = true;
  (function (c, l, a, r, i) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    const t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_ID);
}

function trackPageView(path) {
  if (window.gtag && GA_ID) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

export default function Analytics() {
  const location = useLocation();
  const consentRef = useRef(hasConsent());

  // Load scripts on consent (initial or via banner accept event)
  useEffect(() => {
    if (consentRef.current) {
      loadGA();
      loadClarity();
      trackPageView(location.pathname + location.search);
    }
    const onConsent = (e) => {
      if (e.detail?.choice === "accepted") {
        consentRef.current = true;
        loadGA();
        loadClarity();
        trackPageView(location.pathname + location.search);
      }
    };
    window.addEventListener("lmb-cookie-consent", onConsent);
    return () => window.removeEventListener("lmb-cookie-consent", onConsent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track every route change (SPA)
  useEffect(() => {
    if (consentRef.current) trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return null;
}

// Helper exposed for conversion events
export function trackEvent(name, params = {}) {
  if (window.gtag && hasConsent()) {
    window.gtag("event", name, params);
  }
}
